import React, { useState, useEffect } from "react";
import MainCalculatorsList from "../FoodCalculator/MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";
import imgUrl from "../../../images/pizza.webp";

const TITLE = "حاسبة البيتزا - احسب كمية العجين والمكونات لأي حجم";
const DESCRIPTION =
    "حاسبة البيتزا المجانية - احسب كمية العجين والصلصة والجبن لأي عدد من البيتزا. حدد الحجم وعدد القطع واحصل على الوصفة الدقيقة فوراً.";
const KEYWORDS =
    "حاسبة البيتزا, كمية عجين البيتزا, وصفة البيتزا, حساب مكونات البيتزا, كمية الجبن في البيتزا, pizza calculator, pizza dough calculator";
const OG_URL = "https://do-calculate.com/calculator/ar/pizza/";

const SCHEMA = [
    {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        name: "حاسبة البيتزا",
        description: DESCRIPTION,
        url: OG_URL,
        applicationCategory: "UtilityApplication",
        operatingSystem: "Any",
        offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
        },
    },
    {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            {
                "@type": "ListItem",
                position: 1,
                name: "الرئيسية",
                item: "https://do-calculate.com/",
            },
            {
                "@type": "ListItem",
                position: 2,
                name: "الحاسبات",
                item: "https://do-calculate.com/ar/all-calculators/",
            },
            {
                "@type": "ListItem",
                position: 3,
                name: "حاسبة البيتزا",
                item: OG_URL,
            },
        ],
    },
    {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
            {
                "@type": "Question",
                name: "كم كمية العجين اللازمة لبيتزا قطرها 30 سم؟",
                acceptedAnswer: {
                    "@type": "Answer",
                    text: "لبيتزا قطرها 30 سم (حجم متوسط)، تحتاج إلى حوالي 250 جرام من العجين. هذه الكمية تعتمد على سمك العجين المفضل لديك.",
                },
            },
            {
                "@type": "Question",
                name: "كم كمية الصلصة المناسبة للبيتزا؟",
                acceptedAnswer: {
                    "@type": "Answer",
                    text: "الكمية المناسبة من الصلصة لبيتزا متوسطة الحجم (30 سم) هي حوالي 80-100 جرام. المعدل العام هو 35-40 جرام لكل 100 سم² من مساحة البيتزا.",
                },
            },
            {
                "@type": "Question",
                name: "ما هو الفرق بين أنواع العجين الرقيق والسميك؟",
                acceptedAnswer: {
                    "@type": "Answer",
                    text: "عجين البيتزا الرقيق (ستايل إيطالي) يستخدم حوالي 200-220 جرام لبيتزا 30 سم، بينما العجين السميك (ستايل أمريكي) يحتاج إلى 280-320 جرام لنفس القطر.",
                },
            },
        ],
    },
];

// Pizza size presets (diameter in cm)
const PIZZA_SIZES = {
    small: { name: "صغيرة", diameter: 20 },
    medium: { name: "متوسطة", diameter: 25 },
    large: { name: "كبيرة", diameter: 30 },
    xlarge: { name: "كبيرة جداً", diameter: 35 },
    custom: { name: "مخصص", diameter: 0 },
};

// Dough style multipliers
const DOUGH_STYLES = {
    thin: { name: "عجين رقيق (إيطالي)", factor: 0.85 },
    regular: { name: "عجين عادي (كلاسيك)", factor: 1.0 },
    thick: { name: "عجين سميك (أمريكي)", factor: 1.3 },
    deepdish: { name: "عجين عميق (Deep Dish)", factor: 1.6 },
};

// Base dough weight per cm² of pizza surface (grams) — from stadlermade.com formula
const BASE_DOUGH_PER_CM2 = 0.35; // grams per cm²

function PizzaCalculator() {
    const [sizePreset, setSizePreset] = useState("large");
    const [customDiameter, setCustomDiameter] = useState("");
    const [doughStyle, setDoughStyle] = useState("regular");
    const [numberOfPizzas, setNumberOfPizzas] = useState(1);
    const [hydration, setHydration] = useState(65); // water % relative to flour
    const [saltPercent, setSaltPercent] = useState(2); // salt % relative to flour
    const [yeastPercent, setYeastPercent] = useState(0.3); // yeast % relative to flour
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    const getDiameter = () => {
        if (sizePreset === "custom") {
            return parseFloat(customDiameter || 0);
        }
        return PIZZA_SIZES[sizePreset].diameter;
    };

    const calculatePizza = () => {
        try {
            setError("");
            const diameter = getDiameter();

            if (!diameter || diameter <= 0) {
                setError("يرجى إدخال قطر صحيح للبيتزا");
                setResult(null);
                return;
            }
            if (numberOfPizzas < 1) {
                setError("يرجى إدخال عدد صحيح للبيتزا");
                setResult(null);
                return;
            }

            const styleFactor = DOUGH_STYLES[doughStyle].factor;
            const radius = diameter / 2;
            // Area of pizza circle in cm²
            const areaCm2 = Math.PI * radius * radius;

            // Dough weight for one pizza (grams)
            const doughWeightOnePizza = areaCm2 * BASE_DOUGH_PER_CM2 * styleFactor;
            const totalDoughWeight = doughWeightOnePizza * numberOfPizzas;

            // Baker's percentages — flour is 100%, hydration and salt/yeast are relative to flour
            // totalDough = flour + water + salt + yeast
            // totalDough = flour * (1 + hydration/100 + salt/100 + yeast/100)
            const hydrationFrac = hydration / 100;
            const saltFrac = saltPercent / 100;
            const yeastFrac = yeastPercent / 100;
            const divisor = 1 + hydrationFrac + saltFrac + yeastFrac;

            const flourTotal = totalDoughWeight / divisor;
            const waterTotal = flourTotal * hydrationFrac;
            const saltTotal = flourTotal * saltFrac;
            const yeastTotal = flourTotal * yeastFrac;

            // Sauce: ~35 g per 100 cm² (from omnicalculator.com reference)
            const saucePerPizza = areaCm2 * 0.35;
            const totalSauce = saucePerPizza * numberOfPizzas;

            // Cheese: ~50-60 g per 100 cm² (standard mozzarella)
            const cheesePerPizza = areaCm2 * 0.5;
            const totalCheese = cheesePerPizza * numberOfPizzas;

            // Slices info (standard 8 slices per pizza)
            const totalSlices = numberOfPizzas * 8;

            setResult({
                diameter,
                areaCm2,
                doughWeightOnePizza,
                totalDoughWeight,
                flourTotal,
                waterTotal,
                saltTotal,
                yeastTotal,
                saucePerPizza,
                totalSauce,
                cheesePerPizza,
                totalCheese,
                totalSlices,
                styleName: DOUGH_STYLES[doughStyle].name,
                sizeName: sizePreset === "custom" ? `مخصص (${diameter} سم)` : `${PIZZA_SIZES[sizePreset].name} (${diameter} سم)`,
            });
        } catch (err) {
            setError("خطأ في الحساب: " + err.message);
            setResult(null);
        }
    };

    useEffect(() => {
        calculatePizza();
    }, [sizePreset, customDiameter, doughStyle, numberOfPizzas, hydration, saltPercent, yeastPercent]);

    const handleClear = () => {
        setSizePreset("large");
        setCustomDiameter("");
        setDoughStyle("regular");
        setNumberOfPizzas(1);
        setHydration(65);
        setSaltPercent(2);
        setYeastPercent(0.3);
        setResult(null);
        setError("");
    };

    return (
        <div className="rtl" style={{ direction: "rtl" }}>
            <SharedHelmet
                TITLE={TITLE}
                DESCRIPTION={DESCRIPTION}
                KEYWORDS={KEYWORDS}
                OG_URL={OG_URL}
                SCHEMA={SCHEMA}
                IMAGE={imgUrl}
            />
            <div className="row">
                <div className="col-12 col-md-8">
                    <FirstAds />
                </div>
                <div className="col-12 col-md-4 text-center">
                    <img
                        src={imgUrl}
                        alt={
                            "حاسبة البيتزا - معرفة كميات المكونات بدقة"
                        }
                        width="380px"
                        height="300px"
                    />
                </div>
            </div>
            <div className="row">
                <div className="col-12 col-md-8">
                    <br />
                    <div
                        className="bg-success text-white text-center"
                        style={{ padding: "15px", borderRadius: "5px" }}
                    >
                        <h1 style={{ fontSize: "22px", margin: 0 }}>حاسبة البيتزا 🍕</h1>
                    </div>
                    <div className="border border-light mt-3 p-3 bg-white shadow-sm">
                        {/* Introduction */}
                        <div className="mb-4">
                            <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                                <strong>حاسبة البيتزا</strong> أداة مجانية ودقيقة تساعدك على{" "}
                                <strong>حساب كميات العجين والمكونات</strong> لأي عدد من
                                البيتزا بأي حجم تريده. أدخل قطر البيتزا وعدد القطع ونوع العجين
                                للحصول على وصفة دقيقة فوراً. مثالية للمطاعم والمخابز والطهي
                                المنزلي وتجهيز الحفلات.
                            </p>
                        </div>
                        <hr />
                        <MobileUnderCalAds />
                        {/* Calculator Section */}
                        <div className="calculatorStyle mb-4">
                            <div className="p-3 mb-3 bg-success text-white text-center rounded">
                                <h2 style={{ fontSize: "18px", margin: 0 }}>
                                    احسب مكونات البيتزا بسهولة
                                </h2>
                            </div>

                            {/* Size Selection */}
                            <div className="row">
                                <div className="col-sm-12 col-md-6">
                                    <label htmlFor="sizePreset" >
                                        حجم البيتزا
                                    </label>
                                    <select
                                        id="sizePreset"
                                        className="form-select"
                                        value={sizePreset}
                                        onChange={(e) => setSizePreset(e.target.value)}
                                    >
                                        {Object.entries(PIZZA_SIZES).map(([key, { name, diameter }]) => (
                                            <option key={key} value={key}>
                                                {name}{key !== "custom" ? ` — قطر ${diameter} سم` : ""}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-sm-12 col-md-6">
                                    <label htmlFor="doughStyle" >
                                        نوع العجين
                                    </label>
                                    <select
                                        id="doughStyle"
                                        className="form-select"
                                        value={doughStyle}
                                        onChange={(e) => setDoughStyle(e.target.value)}
                                    >
                                        {Object.entries(DOUGH_STYLES).map(([key, { name }]) => (
                                            <option key={key} value={key}>
                                                {name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Custom Diameter */}
                            {sizePreset === "custom" && (
                                <div className="row">
                                    <div className="col-sm-12">
                                        <label htmlFor="customDiameter" >
                                            قطر البيتزا المخصص (سم)
                                        </label>
                                        <input
                                            id="customDiameter"
                                            type="number"
                                            className="form-control"
                                            value={customDiameter}
                                            onChange={(e) => setCustomDiameter(e.target.value)}
                                            min="10"
                                            max="100"
                                            step="0.5"
                                            placeholder="مثال: 28"
                                            style={{ backgroundColor: "#fff3cd" }}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Number of Pizzas */}
                            <div className="row">
                                <div className="col-sm-12 col-md-6">
                                    <label htmlFor="numberOfPizzas" >
                                        عدد البيتزا
                                    </label>
                                    <input
                                        id="numberOfPizzas"
                                        type="number"
                                        className="form-control"
                                        value={numberOfPizzas}
                                        onChange={(e) => setNumberOfPizzas(parseInt(e.target.value) || 1)}
                                        min="1"
                                        max="100"
                                        placeholder="عدد البيتزا"
                                    />
                                </div>
                            </div>

                            {/* Advanced: Dough Percentages */}
                            <div className="row" style={{ marginTop: "10px" }}>
                                <div className="col-sm-12 col-md-4">
                                    <label >نسبة الماء (%) — Hydration</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={hydration}
                                        onChange={(e) => setHydration(parseFloat(e.target.value) || 65)}
                                        min="50"
                                        max="90"
                                        step="1"
                                        placeholder="65"
                                    />
                                </div>
                                <div className="col-sm-12 col-md-4">
                                    <label >نسبة الملح (%)</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={saltPercent}
                                        onChange={(e) => setSaltPercent(parseFloat(e.target.value) || 2)}
                                        min="0"
                                        max="5"
                                        step="0.1"
                                        placeholder="2"
                                    />
                                </div>
                                <div className="col-sm-12 col-md-4">
                                    <label >نسبة الخميرة (%)</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={yeastPercent}
                                        onChange={(e) => setYeastPercent(parseFloat(e.target.value) || 0.3)}
                                        min="0.1"
                                        max="3"
                                        step="0.1"
                                        placeholder="0.3"
                                    />
                                </div>
                            </div>

                            {/* Clear Button */}
                            <div className="text-center mb-3 mt-4">
                                <button
                                    onClick={handleClear}
                                    className="btn btn-secondary"
                                    style={{ fontSize: "16px", padding: "10px 30px" }}
                                >
                                    امسح
                                </button>
                            </div>

                            {/* Error */}
                            {error && (
                                <div className="alert alert-warning" role="alert">
                                    <strong>تنبيه:</strong> {error}
                                </div>
                            )}

                            {/* Results */}
                            {result && (
                                <div className="mt-4">
                                    <h3
                                        style={{
                                            fontSize: "18px",
                                            marginBottom: "15px",
                                            textAlign: "center",
                                        }}
                                    >
                                        <strong>النتيجة:</strong>
                                    </h3>

                                    {/* Main Results Table */}
                                    <div className="mb-3">
                                        <table className="table bmi-table" style={{ width: "100%" }}>
                                            <thead>
                                                <tr>
                                                    <th className="bmi-header" style={{ textAlign: "center" }}>
                                                        البيان
                                                    </th>
                                                    <th className="bmi-header" style={{ textAlign: "center" }}>
                                                        القيمة
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td style={{ textAlign: "center" }}>وزن العجين لبيتزا واحدة</td>
                                                    <td
                                                        style={{
                                                            textAlign: "center",
                                                            fontSize: "18px",
                                                            fontWeight: "bold",
                                                            color: "#1a5f3f",
                                                        }}
                                                    >
                                                        {result.doughWeightOnePizza.toFixed(0)} جم
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td style={{ textAlign: "center" }}>
                                                        إجمالي العجين ({numberOfPizzas} بيتزا)
                                                    </td>
                                                    <td
                                                        style={{
                                                            textAlign: "center",
                                                            fontSize: "18px",
                                                            fontWeight: "bold",
                                                            color: "#0f3d28",
                                                        }}
                                                    >
                                                        {result.totalDoughWeight.toFixed(0)} جم
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td style={{ textAlign: "center" }}>الدقيق (طحين)</td>
                                                    <td style={{ textAlign: "center", fontSize: "16px" }}>
                                                        {result.flourTotal.toFixed(0)} جم
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td style={{ textAlign: "center" }}>الماء</td>
                                                    <td style={{ textAlign: "center", fontSize: "16px" }}>
                                                        {result.waterTotal.toFixed(0)} مل
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td style={{ textAlign: "center" }}>الملح</td>
                                                    <td style={{ textAlign: "center", fontSize: "16px" }}>
                                                        {result.saltTotal.toFixed(1)} جم
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td style={{ textAlign: "center" }}>الخميرة</td>
                                                    <td style={{ textAlign: "center", fontSize: "16px" }}>
                                                        {result.yeastTotal.toFixed(1)} جم
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td style={{ textAlign: "center" }}>الصلصة (لكل بيتزا)</td>
                                                    <td style={{ textAlign: "center", fontSize: "16px" }}>
                                                        {result.saucePerPizza.toFixed(0)} جم
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td style={{ textAlign: "center" }}>الجبن موتزاريلا (لكل بيتزا)</td>
                                                    <td style={{ textAlign: "center", fontSize: "16px" }}>
                                                        {result.cheesePerPizza.toFixed(0)} جم
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td style={{ textAlign: "center" }}>إجمالي القطع (8 لكل بيتزا)</td>
                                                    <td style={{ textAlign: "center", fontSize: "16px" }}>
                                                        {result.totalSlices} قطعة
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="info-sections-container">
                                        <div className="info-section">
                                            <div className="info-section-header">
                                                <strong>ملخص الوصفة</strong>
                                            </div>
                                            <div className="info-section-body">
                                                <ul style={{ listStyle: "none", padding: 0, lineHeight: "2" }}>
                                                    <li>🍕 <strong>الحجم:</strong> {result.sizeName}</li>
                                                    <li>🍞 <strong>نوع العجين:</strong> {result.styleName}</li>
                                                    <li>
                                                        📐 <strong>مساحة البيتزا:</strong>{" "}
                                                        {result.areaCm2.toFixed(0)} سم²
                                                    </li>
                                                    <li>
                                                        🧂 <strong>إجمالي الصلصة ({numberOfPizzas} بيتزا):</strong>{" "}
                                                        {result.totalSauce.toFixed(0)} جم
                                                    </li>
                                                    <li>
                                                        🧀 <strong>إجمالي الجبن ({numberOfPizzas} بيتزا):</strong>{" "}
                                                        {result.totalCheese.toFixed(0)} جم
                                                    </li>
                                                </ul>
                                                <div
                                                    className="alert alert-info mt-3"
                                                    style={{ fontSize: "14px" }}
                                                >
                                                    <strong>💡 معلومة:</strong> نسبة الماء المستخدمة:{" "}
                                                    {hydration}% — نسبة هيدراشن مثالية للبيتزا المنزلية تتراوح
                                                    بين 60-70%.
                                                </div>
                                            </div>
                                        </div>
                                        <div className="info-section">
                                            <div className="info-section-header">
                                                <strong>تفاصيل الحساب</strong>
                                            </div>
                                            <div className="info-section-body">
                                                <div style={{ lineHeight: "1.8" }}>
                                                    <p>
                                                        <strong>القطر:</strong> {result.diameter} سم
                                                    </p>
                                                    <p>
                                                        <strong>المساحة:</strong> {result.areaCm2.toFixed(2)} سم²
                                                    </p>
                                                    <p>
                                                        <strong>نوع العجين:</strong> {result.styleName}
                                                    </p>
                                                    <hr />
                                                    <p style={{ fontSize: "14px", color: "#666" }}>
                                                        <strong>طريقة الحساب:</strong>
                                                    </p>
                                                    <p style={{ fontSize: "14px", color: "#666" }}>
                                                        المساحة = π × (القطر/2)² = {result.areaCm2.toFixed(2)} سم²
                                                        <br />
                                                        وزن العجين = المساحة × 0.35 جم/سم² × معامل النوع
                                                        <br />
                                                        = {result.areaCm2.toFixed(2)} × 0.35 × {DOUGH_STYLES[doughStyle].factor}
                                                        <br />
                                                        = {result.doughWeightOnePizza.toFixed(0)} جم لكل بيتزا
                                                    </p>
                                                    <p style={{ fontSize: "14px", color: "#666" }}>
                                                        الدقيق = العجين ÷ (1 + هيدراشن + ملح + خميرة)
                                                        <br />
                                                        = {result.totalDoughWeight.toFixed(0)} ÷ (1 + {hydration / 100} + {saltPercent / 100} + {yeastPercent / 100})
                                                        <br />
                                                        = {result.flourTotal.toFixed(0)} جم دقيق
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <hr />
                        {/* Related Links */}
                        <div className="row">
                            <RelatedLinks />
                        </div>
                    </div>
                </div>
                <div className="col-sm-12 col-md-4">
                    <SecondAds />
                    <br />
                    <MainCalculatorsList />
                </div>
            </div>

            {/* Informational Content */}
            <div className="row" style={{ marginTop: "20px" }}>
                <div className="col-12 col-md-8">
                    <div className="info-sections-container">
                        <div className="info-section">
                            <div className="info-section-header">
                                <strong>ما هي حاسبة البيتزا؟</strong>
                            </div>
                            <div className="info-section-body">
                                <p style={{ lineHeight: "1.8" }}>
                                    <strong>حاسبة البيتزا</strong> هي أداة إلكترونية مجانية تحسب
                                    لك كميات جميع مكونات عجينة البيتزا بدقة. بدلاً من التخمين أو
                                    اللجوء لوصفات تقريبية، تعتمد الحاسبة على معادلات الخبازين
                                    الاحترافيين (Baker's Percentages) لإعطائك وصفة مضبوطة تماماً.
                                    تستطيع تعديل حجم البيتزا وعددها ونوع العجين ونسب المكونات
                                    والحصول على النتيجة فوراً.
                                </p>
                                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                                    كيفية استخدام الحاسبة:
                                </h3>
                                <ol style={{ lineHeight: "1.8" }}>
                                    <li>اختر حجم البيتزا من القائمة أو أدخل قطراً مخصصاً</li>
                                    <li>حدد نوع العجين (رقيق، عادي، سميك، أو عميق)</li>
                                    <li>أدخل عدد البيتزا التي تريد تحضيرها</li>
                                    <li>
                                        عدّل نسبة الماء والملح والخميرة حسب الوصفة التي تفضلها
                                    </li>
                                    <li>ستظهر النتيجة تلقائياً وتشمل جميع المكونات بالجرام</li>
                                </ol>
                                <div
                                    className="alert alert-info mt-3"
                                    style={{ lineHeight: "1.8" }}
                                >
                                    <strong>💡 ملاحظة:</strong> الحاسبة تستخدم نظام النسب
                                    المئوية للخبازين (Baker's Percentages) حيث يُعتبر وزن الدقيق
                                    دائماً 100% ويُحسب كل مكون نسبةً منه.
                                </div>
                            </div>
                        </div>

                        <div className="info-section">
                            <div className="info-section-header">
                                <strong>استخدامات حاسبة البيتزا</strong>
                            </div>
                            <div className="info-section-body">
                                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                                    في المطاعم والمحلات التجارية:
                                </h3>
                                <ul style={{ lineHeight: "1.8" }}>
                                    <li>
                                        <strong>ضبط وصفات العجين:</strong> تحديد الكميات الدقيقة
                                        لكل دفعة إنتاج يومي
                                    </li>
                                    <li>
                                        <strong>تقليل الهدر:</strong> تحضير الكمية المناسبة فقط بدون
                                        إسراف
                                    </li>
                                    <li>
                                        <strong>تدريب الموظفين:</strong> وصفة موحدة وواضحة للعمال
                                        الجدد
                                    </li>
                                    <li>
                                        <strong>حساب التكاليف:</strong> معرفة تكلفة المواد الخام لكل
                                        بيتزا
                                    </li>
                                </ul>
                                <h3
                                    style={{
                                        fontSize: "17px",
                                        marginTop: "15px",
                                        marginBottom: "10px",
                                    }}
                                >
                                    في الطهي المنزلي:
                                </h3>
                                <ul style={{ lineHeight: "1.8" }}>
                                    <li>
                                        <strong>حفلات العائلة:</strong> احسب الكميات لعشرة أشخاص أو
                                        خمسين دفعة واحدة
                                    </li>
                                    <li>
                                        <strong>تجربة أحجام مختلفة:</strong> جرب بيتزا 20 سم أو 40
                                        سم بنفس السهولة
                                    </li>
                                    <li>
                                        <strong>الوصفات الاحترافية:</strong> اصنع عجينة نيابوليتانية
                                        حقيقية بنسب صحيحة
                                    </li>
                                    <li>
                                        <strong>التخطيط المسبق:</strong> اشترِ المواد بالكمية الصحيحة
                                        من أول مرة
                                    </li>
                                </ul>
                                <h3
                                    style={{
                                        fontSize: "17px",
                                        marginTop: "15px",
                                        marginBottom: "10px",
                                    }}
                                >
                                    للمخابز والمطابخ الصناعية:
                                </h3>
                                <ul style={{ lineHeight: "1.8" }}>
                                    <li>
                                        <strong>إنتاج بالجملة:</strong> حساب دفعات كبيرة لمئات
                                        البيتزا
                                    </li>
                                    <li>
                                        <strong>توحيد الجودة:</strong> نفس الوصفة في كل مرة دون
                                        تغيير
                                    </li>
                                    <li>
                                        <strong>مقارنة الأنواع:</strong> قارن بين العجين الرقيق
                                        والسميك من حيث التكلفة
                                    </li>
                                    <li>
                                        <strong>توثيق الوصفات:</strong> وصفة مرقمة ودقيقة قابلة
                                        للأرشفة
                                    </li>
                                </ul>
                                <h3
                                    style={{
                                        fontSize: "17px",
                                        marginTop: "15px",
                                        marginBottom: "10px",
                                    }}
                                >
                                    للحفلات والمناسبات:
                                </h3>
                                <ul style={{ lineHeight: "1.8" }}>
                                    <li>
                                        <strong>أعياد الميلاد:</strong> كم بيتزا تحتاج لـ 20 طفلاً؟
                                        الحاسبة تجيب
                                    </li>
                                    <li>
                                        <strong>حفلات العمل:</strong> تخطيط دقيق لكميات الطعام
                                    </li>
                                    <li>
                                        <strong>توزيع القطع:</strong> معرفة عدد القطع الإجمالي للضيوف
                                    </li>
                                    <li>
                                        <strong>التوفير في الميزانية:</strong> لا هدر ولا نقص في
                                        التحضير
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="info-section">
                            <div className="info-section-header">
                                <strong>أنواع عجين البيتزا ونسب الهيدراشن</strong>
                            </div>
                            <div className="info-section-body">
                                <p style={{ lineHeight: "1.8" }}>
                                    الهيدراشن (نسبة الماء للدقيق) هي السر وراء قوام العجين. كلما
                                    زادت النسبة، كان العجين أكثر مرونة وأخف وأصعب في التشكيل. إليك
                                    دليل سريع:
                                </p>
                                <table className="table bmi-table" style={{ marginTop: "1rem" }}>
                                    <thead>
                                        <tr>
                                            <th className="bmi-header" style={{ textAlign: "center" }}>
                                                نوع العجين
                                            </th>
                                            <th className="bmi-header" style={{ textAlign: "center" }}>
                                                نسبة الهيدراشن
                                            </th>
                                            <th className="bmi-header" style={{ textAlign: "center" }}>
                                                القوام
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td style={{ textAlign: "center" }}>إيطالي كلاسيك (نيابوليتاني)</td>
                                            <td style={{ textAlign: "center" }}>60-65%</td>
                                            <td style={{ textAlign: "center" }}>هش، رقيق، بقرمشة خفيفة</td>
                                        </tr>
                                        <tr>
                                            <td style={{ textAlign: "center" }}>نيويوركي</td>
                                            <td style={{ textAlign: "center" }}>63-67%</td>
                                            <td style={{ textAlign: "center" }}>مرن، قابل للطي، لذيذ بارد</td>
                                        </tr>
                                        <tr>
                                            <td style={{ textAlign: "center" }}>أمريكي سميك</td>
                                            <td style={{ textAlign: "center" }}>55-60%</td>
                                            <td style={{ textAlign: "center" }}>سميك، إسفنجي، ناعم</td>
                                        </tr>
                                        <tr>
                                            <td style={{ textAlign: "center" }}>Deep Dish شيكاغو</td>
                                            <td style={{ textAlign: "center" }}>50-55%</td>
                                            <td style={{ textAlign: "center" }}>كثيف، طبقات، يُخبز في قالب</td>
                                        </tr>
                                        <tr>
                                            <td style={{ textAlign: "center" }}>نان بان (Focaccia)</td>
                                            <td style={{ textAlign: "center" }}>75-80%</td>
                                            <td style={{ textAlign: "center" }}>إسفنجي جداً، فقاعات كبيرة</td>
                                        </tr>
                                    </tbody>
                                </table>
                                <div
                                    className="alert alert-success mt-3"
                                    style={{ lineHeight: "1.8" }}
                                >
                                    <strong>✓ نصيحة الخبير:</strong> للمبتدئين، ابدأ بنسبة
                                    هيدراشن 65%. العجين يكون متوازناً وسهل التشكيل ويعطي نتيجة
                                    ممتازة في الفرن المنزلي.
                                </div>
                            </div>
                        </div>

                        <div className="info-section">
                            <div className="info-section-header">
                                <strong>أمثلة عملية</strong>
                            </div>
                            <div className="info-section-body">
                                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                                    مثال 1: بيتزا منزلية واحدة (قطر 30 سم، عجين عادي)
                                </h3>
                                <ul style={{ lineHeight: "1.8" }}>
                                    <li>وزن العجين الكلي: 250 جم</li>
                                    <li>دقيق: 148 جم — ماء: 96 مل — ملح: 3 جم — خميرة: 0.4 جم</li>
                                    <li>صلصة: 83 جم — جبن موتزاريلا: 118 جم</li>
                                </ul>
                                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                                    مثال 2: حفلة 5 بيتزا (قطر 35 سم، عجين رقيق)
                                </h3>
                                <ul style={{ lineHeight: "1.8" }}>
                                    <li>وزن العجين لبيتزا واحدة: 289 جم — الإجمالي: 1446 جم</li>
                                    <li>دقيق: 854 جم — ماء: 555 مل — ملح: 17 جم — خميرة: 2.6 جم</li>
                                    <li>إجمالي الصلصة: 577 جم — إجمالي الجبن: 962 جم</li>
                                </ul>
                                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                                    مثال 3: مطعم — 20 بيتزا يومياً (قطر 30 سم، عجين عادي)
                                </h3>
                                <ul style={{ lineHeight: "1.8" }}>
                                    <li>إجمالي العجين: 5008 جم (حوالي 5 كيلو)</li>
                                    <li>دقيق: 2964 جم — ماء: 1927 مل — ملح: 59 جم — خميرة: 9 جم</li>
                                    <li>صلصة: 3335 جم — جبن: 4718 جم</li>
                                </ul>
                                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                                    مثال 4: بيتزا صغيرة للأطفال (قطر 20 سم، عجين سميك)
                                </h3>
                                <ul style={{ lineHeight: "1.8" }}>
                                    <li>وزن العجين: 143 جم — دقيق: 85 جم — ماء: 55 مل</li>
                                    <li>صلصة: 37 جم — جبن: 52 جم</li>
                                </ul>
                            </div>
                        </div>

                        <div className="info-section">
                            <div className="info-section-header">
                                <strong>نصائح للاستخدام الصحيح وأخطاء شائعة</strong>
                            </div>
                            <div className="info-section-body">
                                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                                    نصائح للحصول على أفضل عجينة:
                                </h3>
                                <ul style={{ lineHeight: "1.8" }}>
                                    <li>
                                        <strong>زن المكونات بالجرام:</strong> الميزان أدق من أكواب
                                        القياس — فارق 10 جم يؤثر على الملمس النهائي
                                    </li>
                                    <li>
                                        <strong>درجة حرارة الماء:</strong> استخدم ماء فاتراً (27-32
                                        درجة) لتنشيط الخميرة بشكل مثالي
                                    </li>
                                    <li>
                                        <strong>الراحة الطويلة:</strong> اترك العجين في الثلاجة
                                        24-72 ساعة للحصول على نكهة أعمق وهضم أسهل
                                    </li>
                                    <li>
                                        <strong>الدقيق المناسب:</strong> استخدم دقيق بروتين عالٍ
                                        (13-14%) لعجينة أكثر مرونة ومطاطة
                                    </li>
                                </ul>
                                <h3
                                    style={{
                                        fontSize: "17px",
                                        marginTop: "15px",
                                        marginBottom: "10px",
                                    }}
                                >
                                    أخطاء شائعة يجب تجنبها:
                                </h3>
                                <ul style={{ lineHeight: "1.8" }}>
                                    <li>
                                        <strong>زيادة الخميرة:</strong> لا تزد عن 1% في الوصفات
                                        العادية — الإكثار منها يعطي طعماً حاداً ونتيجة غير متوقعة
                                    </li>
                                    <li>
                                        <strong>العجن غير الكافي:</strong> اعجن العجينة لا تقل عن
                                        8-10 دقائق يدوياً أو 5 دقائق بالعجان الكهربائي
                                    </li>
                                    <li>
                                        <strong>فرن منخفض الحرارة:</strong> البيتزا تحتاج فرناً
                                        ساخناً جداً — 250 درجة على الأقل للحصول على قاعدة مقرمشة
                                    </li>
                                    <li>
                                        <strong>كثرة المكونات:</strong> الجبن والصلصة الزائدة تجعل
                                        القاعدة مبللة — اتبع الكميات الموصى بها في الحاسبة
                                    </li>
                                </ul>
                                <div
                                    className="alert alert-info mt-3"
                                    style={{ lineHeight: "1.8" }}
                                >
                                    <strong>💡 نصيحة احترافية:</strong> الكميات الموضحة في
                                    الحاسبة هي للعجين خام قبل الخبز. بعد الخبز ينقص الوزن
                                    بنسبة 10-15% بسبب تبخر الماء. خذ هذا بعين الاعتبار عند
                                    حساب الحصص النهائية.
                                </div>
                            </div>
                        </div>

                        {/* FAQ Section */}
                        <div className="info-section">
                            <div className="info-section-header">
                                <strong>الأسئلة الشائعة — حاسبة البيتزا</strong>
                            </div>
                            <div className="info-section-body">
                                <div className="info-sections-container">
                                    <div className="info-section">
                                        <div className="info-section-header">
                                            <h3 style={{ fontSize: "16px" }}>
                                                كم كمية العجين اللازمة لبيتزا 30 سم؟
                                            </h3>
                                        </div>
                                        <div className="info-section-body">
                                            <p style={{ lineHeight: "1.8" }}>
                                                لبيتزا قطرها 30 سم بعجين عادي، تحتاج إلى حوالي 250 جرام
                                                من العجين. إذا كنت تفضل العجين الرقيق الإيطالي، فتراوح
                                                الكمية بين 210-220 جرام، أما العجين السميك الأمريكي
                                                فيحتاج إلى 320-330 جرام. الحاسبة تحسب هذا تلقائياً بناءً
                                                على اختيارك لنوع العجين.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="info-section">
                                        <div className="info-section-header">
                                            <h3 style={{ fontSize: "16px" }}>
                                                ما هو معنى الهيدراشن في عجين البيتزا؟
                                            </h3>
                                        </div>
                                        <div className="info-section-body">
                                            <p style={{ lineHeight: "1.8" }}>
                                                الهيدراشن هو نسبة الماء إلى الدقيق. فمثلاً، هيدراشن 65%
                                                يعني أن لكل 100 جرام دقيق تضيف 65 مل ماء. هذه النسبة
                                                هي الأكثر شيوعاً في بيتزا نيابوليتاني الكلاسيكية. ارفعها
                                                لـ 70% وما فوق إذا أردت عجينة أكثر مرونة وفقاعات أكبر.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="info-section">
                                        <div className="info-section-header">
                                            <h3 style={{ fontSize: "16px" }}>
                                                كم عدد البيتزا الكافي لـ 10 أشخاص؟
                                            </h3>
                                        </div>
                                        <div className="info-section-body">
                                            <p style={{ lineHeight: "1.8" }}>
                                                لـ 10 أشخاص في وجبة رئيسية، يُنصح بـ 3-4 بيتزا حجم
                                                كبير (30 سم). كل بيتزا تُقطع لـ 8 قطع، فتكون 24-32
                                                قطعة — أي 2-3 قطع للشخص الواحد. إذا كان الحفل للأطفال
                                                أو البيتزا مجرد مقبلات، تكفي بيتزتان كبيرتان لعشرة
                                                أشخاص.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="info-section">
                                        <div className="info-section-header">
                                            <h3 style={{ fontSize: "16px" }}>
                                                هل يمكنني تخزين عجين البيتزا؟
                                            </h3>
                                        </div>
                                        <div className="info-section-body">
                                            <p style={{ lineHeight: "1.8" }}>
                                                نعم، العجين المحضّر يُخزَّن في الثلاجة لمدة 3-5 أيام
                                                بعد وضعه في وعاء مغلق مدهون بزيت زيتون خفيف. التخزين
                                                البارد يُحسّن النكهة بشكل ملحوظ — كثير من المطاعم
                                                الإيطالية تخمّر عجينها لـ 48 ساعة أو أكثر لهذا السبب
                                                تحديداً.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="info-section">
                                        <div className="info-section-header">
                                            <h3 style={{ fontSize: "16px" }}>
                                                ما الفرق بين الخميرة الفورية والخميرة الجافة العادية؟
                                            </h3>
                                        </div>
                                        <div className="info-section-body">
                                            <p style={{ lineHeight: "1.8" }}>
                                                الخميرة الفورية (Instant Yeast) تُضاف مباشرة للدقيق دون
                                                نقع مسبق وتعمل أسرع. الخميرة الجافة النشطة (Active Dry)
                                                تحتاج نقعاً في ماء دافئ 10 دقائق قبل الاستخدام. في
                                                حاسبتنا، الكمية المحسوبة تناسب الخميرة الفورية — إذا
                                                استخدمت الجافة العادية أضف 25% إلى الكمية المحسوبة.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="info-section">
                                        <div className="info-section-header">
                                            <h3 style={{ fontSize: "16px" }}>
                                                كم كمية الجبن المناسبة للبيتزا؟
                                            </h3>
                                        </div>
                                        <div className="info-section-body">
                                            <p style={{ lineHeight: "1.8" }}>
                                                الكمية المثالية من جبن الموتزاريلا لبيتزا 30 سم هي
                                                120-150 جرام. أقل من ذلك ستحصل على بيتزا جافة، وأكثر
                                                منه ستصبح القاعدة مبللة. إذا كنت تستخدم جبن ريكوتا أو
                                                شيدر معاً مع الموتزاريلا، قلّل كمية كل نوع وابقَ في
                                                نفس الوزن الإجمالي.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="info-section">
                                        <div className="info-section-header">
                                            <h3 style={{ fontSize: "16px" }}>
                                                ما هي نسبة الملح الصحيحة في عجين البيتزا؟
                                            </h3>
                                        </div>
                                        <div className="info-section-body">
                                            <p style={{ lineHeight: "1.8" }}>
                                                النسبة المثالية للملح في عجين البيتزا هي 2-3% من وزن
                                                الدقيق. أقل من 2% يعطي عجينة بلا طعم، وأكثر من 3%
                                                يُبطئ عمل الخميرة ويؤثر على التخمر. الملح يُضاف دائماً
                                                بعيداً عن الخميرة عند البدء بالعجن — لا تضعهما معاً مباشرة.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="info-section">
                                        <div className="info-section-header">
                                            <h3 style={{ fontSize: "16px" }}>
                                                هل تصلح هذه الحاسبة للاستخدام التجاري في المطاعم؟
                                            </h3>
                                        </div>
                                        <div className="info-section-body">
                                            <p style={{ lineHeight: "1.8" }}>
                                                بالتأكيد! الحاسبة تدعم حساب ما يصل لمئات البيتزا دفعة
                                                واحدة، ما يجعلها مثالية للمطاعم والمخابز. النسب المستخدمة
                                                هي نفسها التي يعتمد عليها الخبازون المحترفون في إيطاليا
                                                وأمريكا. يمكنك ضبط نسبة الهيدراشن والملح والخميرة لتطابق
                                                وصفتك الخاصة وتحفظها للرجوع إليها دائماً.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PizzaCalculator;